import { useEffect, useState } from 'react'
import { subMonths } from 'date-fns'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { createCancelTokenSource } from 'common/api'
import { ReportUpliftLeveledItem } from 'common/api/reporting'
import { useAppSelector } from 'common/hooks/redux'
import { fetchCampaigns } from 'features/campaigns/api'

import {
  ProjectSummaryResponse,
  useActiveProjects,
  useGetBroadcastMetricsQuery,
  useGetBroadcastOrTriggerMetricsQueryFromSnowflake,
  useGetTriggerMetricsQuery,
} from '../../api'
import { useGetMetricsQuery } from '../../hooks/useGetMetricsQuery'

import KPICard from './KPICard'

export type CampaignTypes = 'broadcast' | 'trigger'

interface Props {
  accountId: string
  campaignType: CampaignTypes
}

const calculateMetric = (data: ProjectSummaryResponse, metricName: string) => {
  const results = data?.email_projects?.reduce((acc, currentValue) => {
    if (currentValue[metricName]) {
      acc += currentValue[metricName]
    }
    return acc
  }, 0)
  return results
}

const campaignTypes = {
  broadcast: {
    distributionType: 'broadcast',
    metricsQuery: useGetBroadcastMetricsQuery,
  },
  trigger: {
    distributionType: 'AlwaysOn',
    metricsQuery: useGetTriggerMetricsQuery,
  },
}

const CampaignMetrics = ({ accountId, campaignType }: Props) => {
  const dashboardMode = useAppSelector(
    (state) => state.authStates.dashboardMode
  )

  const { showMetabaseLiveboard } = useFlags()

  const source = createCancelTokenSource()

  const { data: activeProjectsData, hasGQLError: activeProjectsError } =
    useActiveProjects(
      accountId,
      source,
      campaignTypes[campaignType].distributionType
    )

  const activeProjectIds = activeProjectsData ?? []

  const { queryData, hasApiError } = useGetMetricsQuery({
    accountId,
    dashboardMode,
    activeProjectIds,
    getMetricsQuery: !showMetabaseLiveboard
      ? campaignTypes[campaignType].metricsQuery
      : useGetBroadcastOrTriggerMetricsQueryFromSnowflake,
  })

  const [results, setResults] = useState({
    totalIncrementalOpens: 0,
    totalIncrementalClicks: 0,
    totalCampaigns: 0,
    averageOpensUplift: 0,
    averageClicksUplift: 0,
  })

  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (queryData) {
      const data = !showMetabaseLiveboard ? queryData.data.data : queryData.data

      if (!isLoading) {
        setIsLoading(true)
      }
      if (!showMetabaseLiveboard) {
        if (Object.keys(data).length === 0) {
          setIsLoading(false)
        } else if ('email_projects' in data && Object.keys(data).length > 0) {
          const newResults = {
            ...results,
            totalIncrementalOpens: calculateMetric(data, 'incremental_opens'),
            totalIncrementalClicks: calculateMetric(data, 'incremental_clicks'),
            totalCampaigns: calculateMetric(data, 'number_of_campaigns'),
          }
          setResults(newResults)
          setIsLoading(false)
        } else if (Array.isArray(data)) {
          fetchCampaigns({
            variables: {
              page: 1,
              pageSize: 10,
              filter: {
                startDate: subMonths(new Date(), 12).toISOString(),
                endDate: new Date().toISOString(),
                accountId,
                projectsList: activeProjectIds,
              },
            },
            source,
          })
            .then((res) => {
              const foundEntry = (data as ReportUpliftLeveledItem[]).find(
                (entry) => entry.period_label
              )
              if (foundEntry) {
                const upliftsObject = foundEntry.values.find(
                  (value) => value.name === 'Average'
                )

                if (upliftsObject) {
                  const emailOpens = upliftsObject.values.find(
                    (value) => value.label === 'Email Opens'
                  )
                  const emailClicks = upliftsObject.values.find(
                    (value) => value.label === 'Email Clicks'
                  )
                  const newResults = {
                    ...results,
                    averageOpensUplift: emailOpens?.value || 0,
                    averageClicksUplift: emailClicks?.value || 0,
                    totalCampaigns: res.count,
                  }
                  setResults(newResults)
                  setIsLoading(false)
                }
              }
              setIsLoading(false)
            })
            .catch(() => {
              setIsLoading(false)
            })
        }
      } else {
        if (data && (data.trigger || data.broadcast) && showMetabaseLiveboard) {
          const campaignData =
            campaignType === 'broadcast' ? data?.broadcast : data?.trigger

          const newResults = {
            ...results,
            totalIncrementalOpens: campaignData?.opens?.incrementals ?? 0,
            totalIncrementalClicks: campaignData?.clicks?.incrementals ?? 0,
            totalCampaigns: campaignData?.total_campaigns ?? 0,
            averageOpensUplift: campaignData?.opens?.average_uplift ?? 0,
            averageClicksUplift: campaignData?.clicks?.average_uplift ?? 0,
          }
          setResults(newResults)
          setIsLoading(false)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData])

  const hasError = activeProjectsError || hasApiError
  const totalProjects = activeProjectIds ? activeProjectIds.length : 0

  return (
    <KPICard
      campaignType={campaignType}
      hasError={hasError}
      isLoading={isLoading}
      totalProjects={totalProjects}
      totalIncrementalOpens={results.totalIncrementalOpens}
      totalIncrementalClicks={results.totalIncrementalClicks}
      totalCampaigns={results.totalCampaigns}
      averageClicksUplift={results.averageClicksUplift}
      averageOpensUplift={results.averageOpensUplift}
    />
  )
}

export default CampaignMetrics
